import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import MonthStepper from '../../../../components/MonthStepper';
import SegmentedControl from '../../../../components/SegmentedControl';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { FinanceExportSheet } from '../../../../common/interfaces/invoicing.interface';
import { downloadFinancePdfZip, downloadFinanceWorkbook } from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { downloadBlob } from '../../../../utilities/helper/download.helper';
import { currentMonthKey } from '../../../../utilities/helper/date.helper';
import { ExportMode, exportFileName, periodOf, shiftPeriod } from './exportPeriod';

type Choice = FinanceExportSheet | 'pdf';
const CHOICES: Choice[] = ['summary', 'invoices', 'payments', 'balances', 'pdf'];

/**
 * The bookkeeper's report: one workbook with a sheet per subject, for a month, a quarter
 * or a year. The PDFs, when asked for, are a second download - a zip of documents does not
 * belong inside a spreadsheet.
 */
const ExportDialog: React.FC<{ kindergartenId: string; kindergartenName: string; month: string; onClose: () => void }> = ({
  kindergartenId,
  kindergartenName,
  month,
  onClose
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ExportMode>('month');
  const [anchor, setAnchor] = useState(month);
  const [chosen, setChosen] = useState<Choice[]>(['summary', 'invoices', 'payments', 'balances']);
  const [running, setRunning] = useState(false);
  const period = periodOf(mode, anchor);
  const sheets = chosen.filter((choice): choice is FinanceExportSheet => choice !== 'pdf');
  const fileName = exportFileName(kindergartenName, period);
  const atCeiling =
    shiftPeriod(mode, anchor, 1) > currentMonthKey() && periodOf(mode, shiftPeriod(mode, anchor, 1)).from > currentMonthKey();

  const toggle = (choice: Choice) => setChosen(prev => (prev.includes(choice) ? prev.filter(item => item !== choice) : [...prev, choice]));

  const run = async () => {
    setRunning(true);
    try {
      if (sheets.length) {
        const workbook = await downloadFinanceWorkbook(kindergartenId, period.from, period.to, sheets);
        downloadBlob(workbook.blob, workbook.fileName);
      }
      if (chosen.includes('pdf')) {
        const zip = await downloadFinancePdfZip(kindergartenId, period.from, period.to);
        downloadBlob(zip.blob, zip.fileName);
      }
      onClose();
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('finance.export.failed'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open onClose={running ? undefined : onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 760, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600, pb: 0.5 }}>{t('finance.export.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 3 }}>{t('finance.export.dialogDescription')}</Typography>

        <Typography id="export-period-label" sx={{ fontSize: 13, fontWeight: 500, mb: 0.75 }}>
          {t('finance.export.period')}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }} useFlexGap flexWrap="wrap">
          <SegmentedControl
            labelledBy="export-period-label"
            value={mode}
            onChange={setMode}
            width={300}
            options={[
              { value: 'month', label: t('finance.export.month') },
              { value: 'quarter', label: t('finance.export.quarter') },
              { value: 'year', label: t('finance.export.year') }
            ]}
          />
          {mode === 'month' ? (
            <MonthStepper value={anchor} onChange={setAnchor} />
          ) : (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton aria-label={t('finance.export.previous')} onClick={() => setAnchor(shiftPeriod(mode, anchor, -1))}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography sx={{ fontSize: 15, fontWeight: 500, minWidth: 80, textAlign: 'center' }} aria-live="polite">
                {mode === 'quarter' ? period.label.replace(/^(\d{4})-(Q\d)$/, '$2 $1') : period.label}
              </Typography>
              <IconButton
                aria-label={t('finance.export.next')}
                onClick={() => setAnchor(shiftPeriod(mode, anchor, 1))}
                disabled={atCeiling}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>

        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
          <Typography component="legend" sx={{ fontSize: 13, fontWeight: 500, mb: 0.75 }}>
            {t('finance.export.contents')}
          </Typography>
          <Box sx={{ border: 1, borderColor: 'line', borderRadius: '10px' }}>
            {CHOICES.map(choice => (
              <Box key={choice} sx={{ px: 1.5, py: 1, '& + &': { borderTop: 1, borderColor: 'line' } }}>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                  control={<Checkbox checked={chosen.includes(choice)} onChange={() => toggle(choice)} sx={{ mt: -0.5 }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{t(`finance.export.sheets.${choice}.title`)}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {t(`finance.export.sheets.${choice}.description`)}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', wordBreak: 'break-all' }} data-testid="export-file-name">
          {sheets.length ? fileName : ''}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <GenericButton
            buttonType={GenericButtonTypeEnum.Neutral}
            variant="outlined"
            label={t('common.cancel')}
            onClick={onClose}
            disabled={running}
          />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            onClick={run}
            disabled={running || !chosen.length}
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
            label={running ? t('finance.export.preparing') : t('finance.export.download')}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
